import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useSearchInternalProducts from '../../../../../../hooks/products/useSearchInternalProducts';
import SavLines from './SavLines.jsx';

const MAX_QUANTITY_ON_ADD = 10;

const SavLinesForm = ({
  formData,
  handleFormChange,
  formError,
  handleCreateOrUpdateSavOrder,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');

  // Debounce de la recherche pour limiter les appels au BO.
  useEffect(() => {
    const timeout = setTimeout(() => setQuery(inputValue.trim()), 300);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const { products, isLoading } = useSearchInternalProducts(query);

  const handleAddItem = async (selected) => {
    const reference =
      typeof selected === 'string'
        ? selected.trim()
        : selected?.reference?.trim();
    if (!reference) return;

    const title = typeof selected === 'object' ? selected?.title : undefined;
    const stockQuantity =
      typeof selected === 'object' && typeof selected?.stockQuantity === 'number'
        ? selected.stockQuantity
        : undefined;

    const existingLine = (formData?.lines || []).find(
      (obj) => obj.internalProduct === reference
    );

    let newLines;
    if (
      existingLine &&
      existingLine.quantity + 1 <= MAX_QUANTITY_ON_ADD
    ) {
      newLines = [
        { ...existingLine, quantity: existingLine.quantity + 1 },
        ...formData.lines.filter((item) => item.id !== existingLine.id),
      ];
    } else {
      newLines = [
        {
          id: uuidv4(),
          internalProduct: reference,
          reference,
          quantity: 1,
          ...(title !== undefined && { title }),
          ...(stockQuantity !== undefined && { stockQuantity }),
        },
        ...(formData?.lines || []),
      ];
    }

    await handleCreateOrUpdateSavOrder(newLines);
  };

  const invalidReferences = (formData?.lines || [])
    .filter((l) => l.isValid === false)
    .map((l) => l.internalProduct || l.reference);

  const outOfStockReferences = (formData?.lines || [])
    .filter(
      (l) =>
        l.isValid !== false &&
        typeof l.stockQuantity === 'number' &&
        l.stockQuantity < (l.quantity || 1)
    )
    .map((l) => l.internalProduct || l.reference);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
      {formError?.lines?.length > 0 && (
        <FormHelperText error>{formError.lines}</FormHelperText>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <FormControl sx={{ width: '510px' }} variant="outlined">
          <Autocomplete
            freeSolo
            disabled={disabled}
            size="small"
            options={products}
            filterOptions={(options) => options}
            loading={isLoading}
            loadingText="Recherche..."
            noOptionsText={
              query.length < 2
                ? 'Saisissez au moins 2 caractères'
                : 'Aucun produit interne trouvé'
            }
            value={null}
            inputValue={inputValue}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input') setInputValue(value);
              else if (reason === 'clear') setInputValue('');
            }}
            onChange={(_event, selected) => {
              if (!selected) return;
              handleAddItem(selected);
              setInputValue('');
              setQuery('');
            }}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : `${option.reference}${option.title ? ` — ${option.title}` : ''}`
            }
            isOptionEqualToValue={(option, value) =>
              option.reference === value.reference
            }
            renderOption={(props, option) => (
              <li {...props} key={option.reference}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.reference}
                      {option.title ? ` — ${option.title}` : ''}
                    </Typography>
                    <Chip
                      size="small"
                      label={`Stock: ${
                        typeof option.stockQuantity === 'number'
                          ? option.stockQuantity
                          : '—'
                      }`}
                      color={option.stockQuantity > 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </Box>
                  {(option.color || option.packaging) && (
                    <Typography variant="caption" color="text.secondary">
                      {[option.color, option.packaging]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Rechercher par référence ou titre"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary">
            Recherche par référence ou titre. Ex: COM-000001, PCS-004309,
            STI-000357, ACC-000143
          </Typography>
        </FormControl>
      </Box>
      {invalidReferences.length > 0 && (
        <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
          Référence(s) inexistante(s) : {invalidReferences.join(', ')}
        </FormHelperText>
      )}
      {outOfStockReferences.length > 0 && (
        <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
          Stock insuffisant : {outOfStockReferences.join(', ')} — une
          décomposition sera nécessaire.
        </FormHelperText>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <SavLines
          lines={formData?.lines || []}
          handleFormChange={handleFormChange}
          formData={formData}
          formError={formError}
          handleCreateOrUpdateSavOrder={handleCreateOrUpdateSavOrder}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
};

export default SavLinesForm;
